// Login — Curro-branded card on the blue hero. Single Google button (browser
// navigation, not fetch, so the OAuth state/cookie dance works).
// ?denied=1 → invite-only rejection message; ?noGoogle=1 → dev hint.

import { useEffect, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { AuthApi } from "../hooks/useApi.js";
import Icon from "../components/Icon.jsx";

function WfMark() {
  return (
    <span className="mark" style={{
      width: 40, height: 40, borderRadius: 11, display: "grid", placeItems: "center",
      background: "var(--hero)", boxShadow: "0 6px 16px rgba(19,138,110,.32)",
    }}>
      <svg viewBox="0 0 24 24" width="25" height="25" fill="none" stroke="#fff"
        strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 21V10" />
        <path d="M12 12C12 7.5 8.5 5 3.5 5 3.5 9.5 7 12 12 12Z" />
        <path d="M12 13.5c0-3.6 3.2-5.8 8.5-5.8 0 3.6-3.2 5.8-8.5 5.8Z" />
      </svg>
    </span>
  );
}

export default function Login() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const returnUrl = params.get("returnUrl") || "/";
  const denied = params.get("denied") === "1";

  const [cfg, setCfg] = useState(null);
  const [email, setEmail] = useState("");
  const [devErr, setDevErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // Email + password sign-in (for users without a Google account).
  const [pwEmail, setPwEmail] = useState("");
  const [pwPass, setPwPass] = useState("");
  const [pwErr, setPwErr] = useState(null);
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    AuthApi.config()
      .then((c) => { setCfg(c); if (c.defaultEmail) setEmail(c.defaultEmail); })
      .catch(() => setCfg({ googleConfigured: false, devSignin: false }));
  }, []);

  const devSignIn = async (e) => {
    e.preventDefault();
    setDevErr(null);
    setBusy(true);
    try {
      await AuthApi.devSignin(email.trim());
      window.location.href = returnUrl;   // full reload → AuthProvider picks up the new session cookie
    } catch (err) {
      setDevErr(err.body?.error ?? "Sign-in failed. Is this email a registered user?");
      setBusy(false);
    }
  };

  const passwordSignIn = async (e) => {
    e.preventDefault();
    setPwErr(null);
    setPwBusy(true);
    try {
      await AuthApi.passwordSignin(pwEmail.trim(), pwPass);
      window.location.href = returnUrl;   // full reload → AuthProvider picks up the new session cookie
    } catch (err) {
      setPwErr(err.body?.error ?? "Invalid email or password.");
      setPwBusy(false);
    }
  };

  if (!loading && user) return <Navigate to={returnUrl} replace />;

  // Show Google when it's wired. The dev sign-in form is available whenever the
  // server reports it (Development only) — even alongside Google — so local dev
  // never depends on Google's account chooser or a localhost redirect URI.
  const showGoogle = cfg?.googleConfigured ?? true;
  const showDev = !!cfg?.devSignin;

  return (
    <div className="login-page">
      <div className="login-stack">
        <div className="login-card rise">
          <div className="login-brand">
            <WfMark />
            <span className="word">Westfer<span className="dot">t</span></span>
          </div>
          <h1>Sales Analytics</h1>
          <p className="sub">Sales, deliveries &amp; commissions analytics for the Westfert portfolio.</p>

          {denied && (
            <div className="login-error">
              This Google account is not registered. Access is invite-only —
              ask your administrator to add your email.
            </div>
          )}
          {showGoogle && (
            <button
              type="button"
              className="google-btn"
              onClick={() => { window.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`; }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.4 17.7 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/>
                <path fill="#FBBC05" d="M10.5 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.9-6.2z"/>
                <path fill="#34A853" d="M24 48c6.3 0 11.6-2.1 15.5-5.7l-7.7-6c-2.1 1.4-4.8 2.3-7.8 2.3-6.3 0-11.6-3.9-13.5-9.4l-7.9 6.2C6.5 42.6 14.6 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>
          )}

          {/* Email + password — for users without a Google account (admin-provisioned). */}
          <form onSubmit={passwordSignIn} style={{ display: "grid", gap: 10, marginTop: showGoogle ? 14 : 2 }}>
            {showGoogle && <div className="login-or" style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, margin: "2px 0" }}>or sign in with email</div>}
            <input
              type="email" value={pwEmail} onChange={(e) => setPwEmail(e.target.value)} required
              placeholder="you@company.co.za" autoComplete="username"
              style={{ padding: "11px 13px", borderRadius: 10, border: "1px solid rgba(120,140,170,.4)", font: "inherit", width: "100%", boxSizing: "border-box" }}
            />
            <input
              type="password" value={pwPass} onChange={(e) => setPwPass(e.target.value)} required
              placeholder="Password" autoComplete="current-password"
              style={{ padding: "11px 13px", borderRadius: 10, border: "1px solid rgba(120,140,170,.4)", font: "inherit", width: "100%", boxSizing: "border-box" }}
            />
            <button type="submit" className="btn primary" disabled={pwBusy || !pwEmail.trim() || !pwPass} style={{ width: "100%", justifyContent: "center" }}>
              {pwBusy ? "Signing in…" : "Sign in with email"}
            </button>
            {pwErr && <div className="login-error" style={{ marginTop: 2 }}>{pwErr}</div>}
          </form>

          {showDev && (
            <form onSubmit={devSignIn} style={{ display: "grid", gap: 10, marginTop: 14 }}>
              {showGoogle && <div className="login-or" style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, margin: "2px 0" }}>or</div>}
              <div className="login-hint" style={{ marginBottom: 2 }}>
                Developer sign-in (local only) — skips Google; signs in directly as a registered user.
              </div>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
                placeholder="you@themediacrowd.co.za"
                style={{ padding: "11px 13px", borderRadius: 10, border: "1px solid rgba(120,140,170,.4)", font: "inherit", width: "100%", boxSizing: "border-box" }}
              />
              <button type="submit" className="btn primary" disabled={busy || !email.trim()} style={{ width: "100%", justifyContent: "center" }}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
              {devErr && <div className="login-error" style={{ marginTop: 2 }}>{devErr}</div>}
            </form>
          )}

          <p className="login-note"><Icon name="shield" size={12} /> Access is invite-only · your administrator manages registrations.</p>
        </div>
        <div className="login-foot">The Media Crowd · Westfert Sales Analytics</div>
      </div>
    </div>
  );
}
